import { createContext, createElement, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "linear-portal-theme";
export const THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = Exclude<ThemePreference, "system">;
export type ThemeStorage = Pick<Storage, "getItem" | "setItem">;

export const parseThemePreference = (value: string | null | undefined): ThemePreference => {
  if (value === "light" || value === "dark" || value === "system") return value;
  return "system";
};

export const resolveTheme = (preference: ThemePreference, prefersDark: boolean): ResolvedTheme => {
  if (preference === "light" || preference === "dark") return preference;
  return prefersDark ? "dark" : "light";
};

export const readStoredThemePreference = (storage: Pick<Storage, "getItem"> | null | undefined): ThemePreference => {
  if (!storage) return "system";
  try {
    return parseThemePreference(storage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
};

export const writeStoredThemePreference = (storage: Pick<Storage, "setItem"> | null | undefined, preference: ThemePreference): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(THEME_STORAGE_KEY, preference);
    return true;
  } catch {
    return false;
  }
};

const getThemeStorage = (): ThemeStorage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getThemeMediaQuery = (): MediaQueryList | null => {
  if (typeof window === "undefined") return null;
  try {
    return typeof window.matchMedia === "function" ? window.matchMedia(THEME_MEDIA_QUERY) : null;
  } catch {
    return null;
  }
};

const readSystemPreference = (): boolean => {
  const mediaQuery = getThemeMediaQuery();
  if (!mediaQuery) return false;
  try {
    return mediaQuery.matches;
  } catch {
    return false;
  }
};

export interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredThemePreference(getThemeStorage()));
  const [prefersDark, setPrefersDark] = useState<boolean>(readSystemPreference);
  const resolvedTheme = resolveTheme(preference, prefersDark);

  const setPreference = useCallback((nextPreference: ThemePreference): void => {
    const next = parseThemePreference(nextPreference);
    writeStoredThemePreference(getThemeStorage(), next);
    setPreferenceState(next);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.themePreference = preference;
    root.dataset.theme = resolvedTheme;
    root.style.colorScheme = resolvedTheme;
  }, [preference, resolvedTheme]);

  useEffect(() => {
    // Subscribe unconditionally: resolveTheme already ignores prefersDark for explicit choices, and a
    // stale value would paint the pre-change appearance for a frame when the user returns to System.
    const mediaQuery = getThemeMediaQuery();
    if (!mediaQuery) return;

    const handleChange = (event: MediaQueryListEvent): void => {
      setPrefersDark(event.matches);
    };
    setPrefersDark(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    if (typeof mediaQuery.addListener !== "function") return;
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
      setPreferenceState(parseThemePreference(event.key === THEME_STORAGE_KEY ? event.newValue : null));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const contextValue = useMemo<ThemeContextValue>(() => ({ preference, resolvedTheme, setPreference }), [preference, resolvedTheme, setPreference]);

  return createElement(ThemeContext.Provider, { value: contextValue }, children);
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};
